import { Link } from "react-router-dom";
import { 
  Shield, 
  Award, 
  Target, 
  Users, 
  CheckCircle,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Quality Assurance",
      description: "We maintain the highest standards of quality in every project, ensuring precision and durability in all our fabrication work."
    },
    {
      icon: Target,
      title: "Precision Engineering",
      description: "Our team employs advanced techniques and state-of-the-art equipment to deliver accurate and reliable results."
    },
    {
      icon: Award,
      title: "Industry Compliance",
      description: "We adhere to all industry standards and regulations, ensuring our work meets the most stringent requirements."
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Our skilled professionals bring decades of combined experience in steel fabrication and engineering."
    }
  ];

  const milestones = [
    { year: "1999", event: "Nassau Engineering established in Dublin" },
    { year: "2005", event: "Expanded into pharmaceutical clean room services" },
    { year: "2010", event: "Opened state-of-the-art fabrication facility" },
    { year: "2015", event: "Introduced advanced BIM services" },
    { year: "2020", event: "Achieved ISO certification" },
    { year: "2024", event: "25 years of engineering excellence" }
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1586333109867-812586269a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzdGVlbCUyMGZhYnJpY2F0aW9uJTIwd2VsZGluZyUyMHNwYXJrcyUyMGluZHVzdHJpYWwlMjBmYWN0b3J5fGVufDB8fHx8MTc3MDA2MDIwNnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              About Us
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Leaders in <span className="text-blue-500">Steel Fabrication</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-3xl mx-auto">
              Nassau Engineering stands unparalleled in the Irish market and engages with numerous 
              esteemed companies within the Pharmaceutical, Construction and Architecture sectors.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
                Our Story
              </p>
              <h2 
                className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                25 Years of <span className="text-blue-500">Engineering Excellence</span>
              </h2>
              <div className="space-y-4 text-slate-400 leading-relaxed">
                <p>
                  Founded in Dublin, Nassau Engineering has grown from a small workshop to become 
                  one of Ireland's leading steel fabrication and engineering consultancies. Our 
                  journey has been defined by an unwavering commitment to quality, precision, and 
                  customer satisfaction.
                </p>
                <p>
                  Over the past 25 years, we have successfully completed hundreds of projects 
                  across diverse sectors including pharmaceutical, construction, architecture, 
                  and manufacturing. Our expertise spans from precision sheet metal fabrication 
                  to complex clean room installations.
                </p>
                <p>
                  Today, we continue to invest in the latest technologies and training to ensure 
                  our team delivers the highest standard of work. Our BIM services and laser 
                  cutting capabilities represent our commitment to staying at the forefront of 
                  the industry.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/5m7xxe8x_DSCF0004.JPG"
                alt="Nassau Engineering facility"
                className="rounded-sm w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950 to-transparent h-32" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Our Values
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              What <span className="text-blue-500">Drives Us</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <Card 
                key={idx}
                data-testid={`value-card-${idx}`}
                className="bg-slate-950 border-slate-800 rounded-sm card-hover"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-600/20 rounded-sm flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 
                    className="text-lg font-bold text-white uppercase tracking-tight mb-3"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {value.title}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section data-testid="timeline-section" className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Our Journey
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Key <span className="text-blue-500">Milestones</span>
            </h2>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-slate-800" />
            
            <div className="space-y-8">
              {milestones.map((milestone, idx) => (
                <div 
                  key={idx}
                  data-testid={`milestone-${idx}`}
                  className={`relative flex items-center ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                    <div className={`bg-slate-900 p-6 rounded-sm border border-slate-800 inline-block`}>
                      <span className="text-blue-500 font-bold text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
                        {milestone.year}
                      </span>
                      <p className="text-slate-300 mt-2">{milestone.event}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-slate-950" />
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
                Why Choose Us
              </p>
              <h2 
                className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-8"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Your Trusted <span className="text-blue-500">Engineering Partner</span>
              </h2>
              <ul className="space-y-4">
                {[
                  "Over 25 years of industry experience",
                  "State-of-the-art fabrication facilities",
                  "ISO certified quality management",
                  "Expert team of skilled engineers",
                  "On-time project delivery",
                  "Competitive pricing",
                  "Free estimates within 24 hours",
                  "Full project management services"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/contact">
                  <Button 
                    data-testid="about-cta-btn"
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider btn-glow"
                  >
                    Get in Touch
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/6s9gtdyf_cnc-laser-cutting-of-metal-modern-industrial-tech-2023-02-01-19-21-50-utc.jpg"
                alt="Laser cutting"
                className="rounded-sm w-full h-48 object-cover"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/p5ry1xqt_MU-BIM-Image-1024x670.jpg"
                alt="BIM Services"
                className="rounded-sm w-full h-48 object-cover mt-8"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/ni6kzttb_clean-room-luminaires-3.jpg"
                alt="Clean room"
                className="rounded-sm w-full h-48 object-cover -mt-8"
              />
              <img
                src="https://images.unsplash.com/photo-1739599211500-74e04a9ca175?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdGVlbCUyMGFyY2hpdGVjdHVyZSUyMGJ1aWxkaW5nJTIwc3RydWN0dXJlfGVufDB8fHx8MTc3MDA2MDIyMnww&ixlib=rb-4.1.0&q=85"
                alt="Steel structure"
                className="rounded-sm w-full h-48 object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
